import { AutoSizer, Table as VirtualizedTable } from 'react-virtualized';
import { Typography, darken, Pagination } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import cx from 'classnames';
import { withStyles } from 'tss-react/mui';

import ArchPaper from '../../molecules/ArchPaper';
import ArchTableContext from './ArchTableContext';
import ArchTableSpacerColumn from './ArchTableSpacerColumn';
import ArchTableToolbar from '../../molecules/ArchTableToolbar';
import { MAX_COLUMN_WIDTH, totalWidth } from './tableLayout';

const styles = theme => ({
  root: {
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    // border: '1px solid #e0e0e0',
  },
  head: {
    backgroundColor: theme.palette.background.default,
  },
  tableHeader: {
    backgroundColor: theme.palette.background.default,
  },
  tableRow: {
    // borderBottom: '1px solid #e0e0e0',
  },
  tableRowHover: {
    '&:hover': {
      backgroundColor: darken(theme.palette.background.paper, 0.1),
    },
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  table: {
    flex: 1,
  },

  tableContainer: {
    flex: 1,
    // overflowY: 'auto',
  },
  // react-virtualized puts the table's width on both the header row and the row
  // grid, so a table wider than its container scrolls as one piece in here. The
  // rows keep their own vertical scrolling.
  scroller: {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  empty: {
    margin: 'auto',
    height: '100%',
    display: 'flex',
  },
  emptyText: {
    margin: 'auto',
    textAlign: 'center',
  },
  pagination: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
  },
});

class ArchTable extends React.Component {
  getRowClassName = ({ index }) => {
    const { classes } = this.props;

    return cx(classes.tableRow, classes.flexContainer, {
      [classes.tableHeader]: index === -1,
      [classes.tableRowHover]: index > -1,
    });
  };

  // Turns one of the layout's descriptors back into a Column: the width the user
  // chose, no flex of its own, and a resize handle for the header to hang off.
  renderColumn(column) {
    const { onColumnResize } = this.props;

    return React.cloneElement(column.element, {
      key: column.id,
      width: column.width,
      flexGrow: 0,
      flexShrink: 0,
      // react-virtualized gives body cells overflow:hidden but leaves header
      // cells at overflow:visible, so a header's automatic minimum size is its
      // label's min-content width while the body shrinks to the width asked for.
      // Drag past the label and the two stop lining up. This levels them.
      headerStyle: { ...column.element.props.headerStyle, overflow: 'hidden' },
      // Merged, never replaced: the checkbox column keeps its click and
      // select-all handlers in here.
      columnData: {
        ...column.element.props.columnData,
        resize:
          column.pinned || !onColumnResize
            ? undefined
            : {
                label: column.label,
                width: column.width,
                minWidth: column.minWidth,
                maxWidth: MAX_COLUMN_WIDTH,
                onResize: (px, options) =>
                  onColumnResize(column.id, px, options),
              },
      },
    });
  }

  renderColumns() {
    const shown = this.props.columns.filter(column => !column.hidden);
    // The spacer goes before the columns pinned to the end, not after them, or the
    // row actions would float away from the right-hand edge on a wide screen.
    const trailing = shown.findIndex(column => column.pinned === 'end');
    const at = trailing === -1 ? shown.length : trailing;
    const render = column => this.renderColumn(column);

    return [
      ...shown.slice(0, at).map(render),
      <ArchTableSpacerColumn key="__spacer" />,
      ...shown.slice(at).map(render),
    ];
  }

  render() {
    const {
      classes,
      title,
      loading,
      children,
      columns,
      rowGetter,
      rowCount,
      sortBy,
      sortDirection,
      sort,
      numSelected,
      onHeaderClick,
      onCheckboxClick,
      renderToolbar,
      className,
      rowStyle,
      rowHeight,
      minWidth,
      square,
      elevation,
      pagination,
      currentPage,
      pageCount,
      onPageChange,
    } = this.props;
    const laidOut = Boolean(columns);
    const contentWidth = laidOut ? totalWidth(columns) : 0;
    return (
      <ArchTableContext.Provider value={rowHeight}>
        <ArchPaper
          className={cx(className, classes.root)}
          square={square}
          elevation={elevation}
          fullHeight
        >
          {renderToolbar ? (
            renderToolbar()
          ) : (
            <ArchTableToolbar
              loading={loading}
              title={title}
              numSelected={numSelected}
            />
          )}
          <div className={classes.tableContainer}>
            <AutoSizer>
              {({ height, width }) => (
                <div className={classes.scroller} style={{ height, width }}>
                  <VirtualizedTable
                    sortBy={sortBy}
                    sort={sort}
                    sortDirection={sortDirection}
                    className={classes.table}
                    rowCount={rowCount}
                    rowGetter={rowGetter}
                    width={Math.max(width, minWidth, contentWidth)}
                    height={height}
                    rowHeight={rowHeight}
                    headerHeight={48}
                    rowClassName={this.getRowClassName}
                    onHeaderClick={onHeaderClick}
                    onCheckboxClick={onCheckboxClick}
                    rowStyle={rowStyle}
                    noRowsRenderer={() =>
                      loading ? null : (
                        <div className={classes.empty}>
                          <Typography
                            className={classes.emptyText}
                            variant="body2"
                          >
                            No items match your current selection
                          </Typography>{' '}
                        </div>
                      )
                    }
                  >
                    {laidOut ? this.renderColumns() : children}
                  </VirtualizedTable>
                </div>
              )}
            </AutoSizer>
          </div>

          {pagination && (
            <div className={classes.pagination}>
              <div />
              <Pagination
                showFirstButton
                showLastButton
                count={pageCount}
                page={currentPage}
                variant="outlined"
                shape="rounded"
                onChange={(event, page) => onPageChange(event, page)}
              />
            </div>
          )}
        </ArchPaper>
      </ArchTableContext.Provider>
    );
  }
}

ArchTable.propTypes = {
  // Either hand the columns over as children, the way every table has until now,
  // or pass the descriptors from useTableLayout and let the user arrange them.
  children: PropTypes.node,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      element: PropTypes.element.isRequired,
      width: PropTypes.number.isRequired,
      hidden: PropTypes.bool,
      pinned: PropTypes.string,
    }),
  ),
  onColumnResize: PropTypes.func,
};

ArchTable.defaultProps = {
  children: null,
  columns: null,
  onColumnResize: null,
  onCheckboxClick: null,
  onHeaderClick: null,
  renderToolbar: null,
  loading: false,
  className: '',
  rowStyle: {},
  rowHeight: 48,
  minWidth: 800,
};

export default withStyles(ArchTable, styles);
